import { useState, useMemo } from 'react';
import { useQuery, useMutation } from 'urql';
import { useNavigate, NavLink } from 'react-router-dom';
import { Q_DRAFTS, M_APPROVE_DRAFT, M_REQUEST_CHANGES, M_SCHEDULE_DRAFT, M_DELETE_DRAFT } from '@/lib/queries';
import { useUi } from '@/stores/ui.store';

const STATUS_STYLE: Record<string, string> = {
  draft:      'bg-stone-100 text-stone-700',
  pending:    'bg-amber-50 text-amber-700',
  approved:   'bg-blue-50 text-blue-700',
  scheduled:  'bg-violet-50 text-violet-700',
  publishing: 'bg-indigo-50 text-indigo-700',
  published:  'bg-emerald-50 text-emerald-700',
  failed:     'bg-red-50 text-red-700',
  rejected:   'bg-red-50 text-red-700',
};

/**
 * Read the most recent failure from the post's `lastError` field — sourced
 * server-side from the `publish_attempts` table, which carries the structured
 * `category` (auth|rate_limit|transient|content_policy|media|unknown),
 * `retryable`, `code`, and `httpStatus`. `isAuth` ⇒ show Reconnect instead of
 * Retry; `retryable` ⇒ a Retry is worth offering.
 */
function lastFailure(d: any): { message: string | null; isAuth: boolean; retryable: boolean } | null {
  if (d?.status !== 'failed') return null;
  const e = d?.lastError;
  if (!e) return { message: null, isAuth: false, retryable: true };
  return {
    message: e.message ?? null,
    isAuth: !!e.isAuth,
    retryable: e.retryable !== false,
  };
}

/**
 * Read X thread progress from the post's `meta` JSON. The worker writes
 * `meta.thread` = ordered `[{ text, media_ids }]` and `meta.thread_progress` =
 * `[{ remote_id, status: 'pending'|'succeeded'|'failed' }]` as it posts each
 * segment. Returns null for non-thread posts.
 */
function threadProgress(d: any): { total: number; posted: number; failedIndex: number | null; partial: boolean } | null {
  const meta = d?.meta;
  const segs = meta?.thread;
  if (!Array.isArray(segs) || segs.length === 0) return null;
  const prog: any[] = Array.isArray(meta?.thread_progress) ? meta.thread_progress : [];
  const posted = prog.filter((p) => p?.status === 'succeeded').length;
  const failedIndex = prog.findIndex((p) => p?.status === 'failed');
  return {
    total: segs.length,
    posted,
    failedIndex: failedIndex >= 0 ? failedIndex : null,
    partial: posted > 0 && posted < segs.length,
  };
}

const STATUS_FILTERS = ['', 'draft', 'pending', 'approved', 'scheduled', 'publishing', 'published', 'failed', 'rejected'];
const PLATFORMS      = ['', 'linkedin', 'twitter', 'facebook', 'instagram', 'tiktok', 'youtube', 'threads', 'pinterest'];
const SORTS = [
  { id: 'newest',    label: 'Newest first' },
  { id: 'oldest',    label: 'Oldest first' },
  { id: 'scheduled', label: 'Scheduled time' },
];

export default function DraftsRoute() {
  const nav = useNavigate();
  const [drafts, refetch] = useQuery({ query: Q_DRAFTS, variables: { limit: 200 } });
  const [, approve]        = useMutation(M_APPROVE_DRAFT);
  const [, requestChanges] = useMutation(M_REQUEST_CHANGES);
  const [, scheduleDraft]  = useMutation(M_SCHEDULE_DRAFT);
  const [, deleteDraft]    = useMutation(M_DELETE_DRAFT);
  const { toast } = useUi();

  // Filters
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [platform, setPlatform] = useState('');
  const [sort, setSort]       = useState('newest');

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());

  const list: any[] = drafts.data?.drafts?.data ?? [];

  const filtered = useMemo(() => {
    let rows = list;
    if (status)   rows = rows.filter((d) => d.status === status);
    if (platform) rows = rows.filter((d) => d.platform === platform);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((d) => (d.caption ?? '').toLowerCase().includes(q));
    }
    const sorted = [...rows];
    if (sort === 'oldest')         sorted.sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
    else if (sort === 'scheduled') sorted.sort((a, b) => (new Date(a.scheduledAt ?? 0).getTime() || Infinity) - (new Date(b.scheduledAt ?? 0).getTime() || Infinity));
    else                            sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return sorted;
  }, [list, status, platform, search, sort]);

  const allSelected = filtered.length > 0 && filtered.every((d) => selected.has(d.id));
  const selectAll = () => {
    if (allSelected) clearSel();
    else setSelected(new Set(filtered.map((d) => d.id)));
  };

  // Bulk actions
  const [busy, setBusy] = useState(false);
  const bulkApprove = async () => {
    setBusy(true);
    const ids = Array.from(selected);
    const results = await Promise.all(ids.map((id) => approve({ id })));
    setBusy(false);
    const ok = results.filter((r) => !r.error).length;
    toast(`${ok}/${ids.length} approved`);
    clearSel();
    refetch({ requestPolicy: 'network-only' });
  };
  const bulkSchedule = async () => {
    const whenStr = prompt('Schedule all selected to (YYYY-MM-DD HH:MM, local time):',
      new Date(Date.now() + 30 * 60_000).toISOString().slice(0, 16).replace('T', ' '));
    if (!whenStr) return;
    const when = new Date(whenStr.replace(' ', 'T'));
    if (isNaN(+when) || when.getTime() < Date.now() + 60_000) return toast('Pick a time at least 1 min in the future', 'warning');
    setBusy(true);
    const ids = Array.from(selected);
    const results = await Promise.all(ids.map((id) => scheduleDraft({ id, when: when.toISOString() })));
    setBusy(false);
    const ok = results.filter((r) => !r.error).length;
    toast(`${ok}/${ids.length} scheduled for ${when.toLocaleString()}`);
    clearSel();
    refetch({ requestPolicy: 'network-only' });
  };
  const bulkDelete = async () => {
    const n = selected.size;
    if (!confirm(`Delete ${n} draft${n > 1 ? 's' : ''}? This is reversible by an admin.`)) return;
    setBusy(true);
    const ids = Array.from(selected);
    const results = await Promise.all(ids.map((id) => deleteDraft({ id })));
    setBusy(false);
    const ok = results.filter((r) => !r.error).length;
    toast(`${ok}/${n} deleted`);
    clearSel();
    refetch({ requestPolicy: 'network-only' });
  };

  // Single-row actions
  const handleApprove = async (id: string) => {
    const r = await approve({ id });
    if (r.error) toast(r.error.message, 'error');
    else { toast('Approved'); refetch({ requestPolicy: 'network-only' }); }
  };
  const handleRequestChanges = async (id: string) => {
    const comment = prompt('What needs to change?');
    if (!comment) return;
    const r = await requestChanges({ id, comment });
    if (r.error) toast(r.error.message, 'error');
    else { toast('Changes requested'); refetch({ requestPolicy: 'network-only' }); }
  };
  const handlePublishNow = async (id: string) => {
    const when = new Date(Date.now() + 90_000).toISOString();
    const r = await scheduleDraft({ id, when });
    if (r.error) toast(r.error.message, 'error');
    else { toast('Scheduled — should publish in ~1–2 min'); refetch({ requestPolicy: 'network-only' }); }
  };
  // Retry a failed post: re-schedule it (~90s out) so the worker's due-poller
  // re-attempts publishing. Reuses scheduleDraft — there's no dedicated retry
  // mutation, and the publish status-lock accepts rows in status 'failed'.
  const handleRetry = async (id: string) => {
    const when = new Date(Date.now() + 90_000).toISOString();
    const r = await scheduleDraft({ id, when });
    if (r.error) toast(r.error.message, 'error');
    else { toast('Re-queued — should retry in ~1–2 min'); refetch({ requestPolicy: 'network-only' }); }
  };
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this draft?')) return;
    const r = await deleteDraft({ id });
    if (r.error) toast(r.error.message, 'error');
    else { toast('Deleted'); refetch({ requestPolicy: 'network-only' }); }
  };

  if (drafts.fetching) return <div className="p-8 text-stone-500">Loading drafts…</div>;
  if (drafts.error)    return <div className="p-8 text-red-600">Failed: {drafts.error.message}</div>;

  return (
    <div className="p-8 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Drafts</h1>
          <p className="text-sm text-stone-500 mt-1">Posts not yet published — approve, schedule, or refine.</p>
        </div>
        <button onClick={() => nav('/app/create')} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium">+ New post</button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search caption…"
            className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500" />
          <span className="absolute left-3 top-2 text-stone-400">⌕</span>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
          {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : 'All statuses'}</option>)}
        </select>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
          {PLATFORMS.map((p) => <option key={p} value={p}>{p || 'All platforms'}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
          {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <span className="text-xs text-stone-400 ml-auto">{filtered.length} of {list.length}</span>
      </div>

      {/* Bulk action toolbar */}
      {selected.size > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <p className="text-sm font-medium text-violet-900">{selected.size} selected</p>
          <div className="flex items-center gap-2">
            <button onClick={bulkApprove} disabled={busy} className="px-3 py-1.5 text-xs bg-violet-600 hover:bg-violet-700 text-white rounded font-medium disabled:opacity-50">Approve all</button>
            <button onClick={bulkSchedule} disabled={busy} className="px-3 py-1.5 text-xs border border-violet-300 text-violet-700 hover:bg-violet-100 rounded font-medium disabled:opacity-50">Schedule all</button>
            <button onClick={bulkDelete} disabled={busy} className="px-3 py-1.5 text-xs border border-red-200 text-red-700 hover:bg-red-50 rounded font-medium disabled:opacity-50">Delete all</button>
            <button onClick={clearSel} className="text-xs text-stone-500 hover:text-stone-700 ml-2">Clear</button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-xl p-12 text-center">
          <p className="text-stone-600 font-medium">No drafts match these filters.</p>
          <p className="text-sm text-stone-400 mt-1">Try widening the filters, or create a new post.</p>
          <button onClick={() => nav('/app/create')} className="mt-4 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium">+ New post</button>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          {/* Header row with select-all */}
          <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-center gap-3 text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
            <input type="checkbox" checked={allSelected} onChange={selectAll}
              className="h-4 w-4 rounded border-stone-300 accent-violet-600" aria-label="Select all" />
            <span className="flex-1">Caption</span>
            <span className="w-24">Status</span>
            <span className="w-32 text-right">Updated</span>
          </div>
          {/* Rows */}
          <div className="divide-y divide-stone-100">
            {filtered.map((d) => {
              const on = selected.has(d.id);
              const fail = lastFailure(d);
              const thread = threadProgress(d);
              return (
                <div key={d.id} className={`px-4 py-3 flex items-center gap-3 group ${on ? 'bg-violet-50/60' : 'hover:bg-stone-50'}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(d.id)}
                    className="h-4 w-4 rounded border-stone-300 accent-violet-600" />
                  <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-stone-100 text-stone-700 w-20 text-center flex-shrink-0">{d.platform}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-800 line-clamp-2">{d.caption || <span className="text-stone-400">(empty)</span>}</p>
                    <p className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{d.charCount}/{d.charLimit}</span>
                      <span>·</span>
                      <span>{d.postType}</span>
                      {d.aiGenerated && <><span>·</span><span>✨ AI</span></>}
                      {thread && <><span>·</span><span>🧵 {thread.total} tweets</span></>}
                      {d.scheduledAt && <><span>·</span><span>{new Date(d.scheduledAt).toLocaleString()}</span></>}
                    </p>
                    {/* Thread progress — per-segment status from meta.thread_progress. */}
                    {thread && (thread.posted > 0 || thread.failedIndex !== null) && (
                      <p className="text-[11px] mt-1 flex items-center gap-1.5">
                        <span className={thread.failedIndex !== null ? 'text-red-600' : thread.posted === thread.total ? 'text-emerald-600' : 'text-stone-500'}>
                          {thread.posted} of {thread.total} posted
                        </span>
                        {thread.failedIndex !== null && (
                          <span className="text-red-600">· failed at tweet {thread.failedIndex + 1}</span>
                        )}
                      </p>
                    )}
                    {/* Failure reason — surfaced from the post's structured lastError. */}
                    {fail && (
                      <p className="text-[11px] text-red-600 mt-1 flex items-start gap-1.5">
                        <span aria-hidden>⚠️</span>
                        <span className="min-w-0">
                          {fail.isAuth
                            ? 'Connection expired or invalid — reconnect the account to publish.'
                            : (fail.message ?? 'Publishing failed. Retry or check the connection.')}
                        </span>
                      </p>
                    )}
                  </div>
                  <span className={`inline-flex items-center justify-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${STATUS_STYLE[d.status] ?? 'bg-stone-100 text-stone-600'} w-24 flex-shrink-0`}>
                    {d.status === 'publishing' && (
                      <span className="inline-block h-2.5 w-2.5 rounded-full border-2 border-indigo-300 border-t-indigo-600 animate-spin" aria-hidden />
                    )}
                    {d.status === 'published' && <span aria-hidden>●</span>}
                    <span>{d.status}</span>
                  </span>
                  <span className="w-32 text-right text-[11px] text-stone-500 flex-shrink-0">{new Date(d.updatedAt).toLocaleDateString()}</span>
                  {/* Hover actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 ml-2">
                    {(d.status === 'draft' || d.status === 'pending') && (
                      <button onClick={() => handleApprove(d.id)} title="Approve" className="px-2 py-1 text-xs text-violet-700 hover:bg-violet-100 rounded">✓</button>
                    )}
                    {d.status === 'pending' && (
                      <button onClick={() => handleRequestChanges(d.id)} title="Request changes" className="px-2 py-1 text-xs text-amber-700 hover:bg-amber-50 rounded">↻</button>
                    )}
                    {d.status === 'approved' && (
                      <button onClick={() => handlePublishNow(d.id)} title="Publish now" className="px-2 py-1 text-xs text-violet-700 hover:bg-violet-100 rounded">▶</button>
                    )}
                    {d.status === 'failed' && (
                      fail?.isAuth ? (
                        <NavLink to="/app/connections" title="Reconnect account"
                          className="px-2 py-1 text-xs text-amber-700 hover:bg-amber-50 rounded font-medium">Reconnect</NavLink>
                      ) : fail?.retryable !== false ? (
                        // Threads with partial progress resume from the failed
                        // segment automatically — same reschedule, clearer label.
                        <button onClick={() => handleRetry(d.id)} title={thread?.partial ? 'Resume thread from the failed tweet' : 'Retry publishing'}
                          className="px-2 py-1 text-xs text-violet-700 hover:bg-violet-100 rounded font-medium">{thread?.partial ? 'Resume' : 'Retry'}</button>
                      ) : null
                      /* non-retryable (e.g. content_policy) → no Retry; fix the post or delete */
                    )}
                    <button onClick={() => handleDelete(d.id)} title="Delete" className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
